import React from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
function LegalLayout({
  title,
  lastUpdated,
  children
}: {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}) {
  return <div className="pt-32 pb-20 px-6 md:px-12 max-w-4xl mx-auto" data-id="element-1002">
      <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1003">{title}</h1>
      <p className="text-gray-400 mb-12 pb-8 border-b border-white/10" data-id="element-1004">
        Last updated: {lastUpdated}
      </p>
      <div className="space-y-8 text-gray-300 leading-relaxed [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-12 [&_h2]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:text-[#D4AF37] [&_a]:underline" data-id="element-1005">
        {children}
      </div>
    </div>;
}
export function PrivacyPolicyPage() {
  usePageSEO({
    canonical: '/privacy-policy',
    keywords: 'privacy policy, Design Flow Digitals privacy, data protection',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Privacy Policy',
      path: '/privacy-policy'
    }]),
    title: 'Privacy Policy | Design Flow Digitals',
    description: 'Read the privacy policy of Design Flow Digitals. Learn how we collect, use, and protect your personal information.'
  });
  return <LegalLayout title="Privacy Policy" lastUpdated="March 17, 2026" data-id="element-1006">
      <h2 data-id="element-1007">1. Introduction</h2>
      <p data-id="element-1008">
        At Design Flow Digitals ("Company", "we", "us", or "our"), we take your
        privacy seriously. This Privacy Policy explains how we collect, use,
        disclose, and safeguard your information when you visit our website
        designflowdigitals.com or use our services.
      </p>

      <h2 data-id="element-1009">2. Information We Collect</h2>
      <p data-id="element-1010">
        We may collect information about you in a variety of ways. The
        information we may collect includes:
      </p>
      <ul data-id="element-1011">
        <li data-id="element-1012">
          <strong className="text-white" data-id="element-1013">Personal Data:</strong> Personally
          identifiable information, such as your name, email address, shipping
          address, and telephone number that you voluntarily give to us when you
          contact us or place an order.
        </li>
        <li data-id="element-1014">
          <strong className="text-white" data-id="element-1015">Derivative Data:</strong> Information
          our servers automatically collect when you access the Site, such as
          your IP address, browser type, operating system, access times, and the
          pages you have viewed.
        </li>
        <li data-id="element-1016">
          <strong className="text-white" data-id="element-1017">Financial Data:</strong> Financial
          information, such as data related to your payment method, that we may
          collect when you purchase our services. We store only very limited
          financial information that we collect.
        </li>
      </ul>

      <h2 data-id="element-1018">3. Use of Your Information</h2>
      <p data-id="element-1019">We may use information collected about you to:</p>
      <ul data-id="element-1020">
        <li data-id="element-1021">Create and manage your account.</li>
        <li data-id="element-1022">Process transactions and send related information.</li>
        <li data-id="element-1023">Email you regarding your account, orders, or services.</li>
        <li data-id="element-1024">
          Fulfill and manage purchases, orders, payments, and other
          transactions.
        </li>
        <li data-id="element-1025">Improve our website and services.</li>
        <li data-id="element-1026">Respond to product and customer service requests.</li>
      </ul>

      <h2 data-id="element-1027">4. Disclosure of Your Information</h2>
      <p data-id="element-1028">
        We may share information we have collected about you in certain
        situations. Your information may be disclosed as follows:
      </p>
      <ul data-id="element-1029">
        <li data-id="element-1030">
          <strong className="text-white" data-id="element-1031">By Law or to Protect Rights:</strong>{' '}
          If we believe the release of information is appropriate to comply with
          the law or to protect our rights, property, or safety.
        </li>
        <li data-id="element-1032">
          <strong className="text-white" data-id="element-1033">Third-Party Service Providers:</strong>{' '}
          We may share your information with third parties that perform services
          for us, including payment processing, data analysis, email delivery,
          hosting services, and customer service.
        </li>
      </ul>

      <h2 data-id="element-1034">5. Security of Your Information</h2>
      <p data-id="element-1035">
        We use administrative, technical, and physical security measures to help
        protect your personal information. While we have taken reasonable steps
        to secure the personal information you provide to us, please be aware
        that no security measures are perfect or impenetrable.
      </p>

      <h2 data-id="element-1036">6. Contact Us</h2>
      <p data-id="element-1037">
        If you have questions or comments about this Privacy Policy, please
        contact us at:{' '}
        <a href="mailto:support@designflowdigitals.com" data-id="element-1038">
          support@designflowdigitals.com
        </a>
      </p>
      <p data-id="element-1039">
        Design Flow Digitals, Pankaj Maurya LLC, 67 Wigley Loop, Whitwell, TN,
        37397, United States.
      </p>
    </LegalLayout>;
}
export function TermsPage() {
  usePageSEO({
    canonical: '/terms',
    keywords: 'terms of service, Design Flow Digitals terms, user agreement',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Terms of Service',
      path: '/terms'
    }]),
    title: 'Terms of Service | Design Flow Digitals',
    description: 'Read the terms of service for Design Flow Digitals. Understand the terms governing your use of our Shopify development services and products.'
  });
  return <LegalLayout title="Terms of Service" lastUpdated="March 17, 2026" data-id="element-1040">
      <h2 data-id="element-1041">1. Agreement to Terms</h2>
      <p data-id="element-1042">
        These Terms of Service constitute a legally binding agreement made
        between you, whether personally or on behalf of an entity ("you") and
        Design Flow Digitals ("Company", "we", "us", or "our"), concerning your
        access to and use of the designflowdigitals.com website as well as any
        other media form, media channel, or mobile application related, linked,
        or otherwise connected thereto.
      </p>

      <h2 data-id="element-1043">2. Intellectual Property Rights</h2>
      <p data-id="element-1044">
        Unless otherwise indicated, the Site is our proprietary property and all
        source code, databases, functionality, software, website designs, audio,
        video, text, photographs, and graphics on the Site (collectively, the
        "Content") and the trademarks, service marks, and logos contained
        therein are owned or controlled by us or licensed to us, and are
        protected by copyright and trademark laws.
      </p>

      <h2 data-id="element-1045">3. User Representations</h2>
      <p data-id="element-1046">By using the Site, you represent and warrant that:</p>
      <ul data-id="element-1047">
        <li data-id="element-1048">
          All information you submit will be true, accurate, current, and
          complete.
        </li>
        <li data-id="element-1049">
          You will maintain the accuracy of such information and promptly update
          as necessary.
        </li>
        <li data-id="element-1050">
          You have the legal capacity and you agree to comply with these Terms
          of Service.
        </li>
        <li data-id="element-1051">
          You will not use the Site for any illegal or unauthorized purpose.
        </li>
      </ul>

      <h2 data-id="element-1052">4. Services</h2>
      <p data-id="element-1053">
        Design Flow Digitals provides Shopify theme development, Shopify app
        development, custom Shopify store design, and related ecommerce
        services. All services are subject to separate agreements and statements
        of work as applicable.
      </p>

      <h2 data-id="element-1054">5. Payments and Billing</h2>
      <p data-id="element-1055">
        You agree to provide current, complete, and accurate purchase and
        account information for all purchases made via the Site. You agree to
        promptly update your account and payment information so that we can
        complete your transactions and contact you as needed.
      </p>

      <h2 data-id="element-1056">6. Limitation of Liability</h2>
      <p data-id="element-1057">
        In no event will we or our directors, employees, or agents be liable to
        you or any third party for any direct, indirect, consequential,
        exemplary, incidental, special, or punitive damages arising from your
        use of the site or services.
      </p>

      <h2 data-id="element-1058">7. Contact Us</h2>
      <p data-id="element-1059">
        For questions about these Terms of Service, please contact us at:{' '}
        <a href="mailto:support@designflowdigitals.com" data-id="element-1060">
          support@designflowdigitals.com
        </a>
      </p>
    </LegalLayout>;
}
export function RefundPolicyPage() {
  usePageSEO({
    canonical: '/refund-policy',
    keywords: 'refund policy, Design Flow Digitals refunds, return policy',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Refund Policy',
      path: '/refund-policy'
    }]),
    title: 'Refund Policy | Design Flow Digitals',
    description: 'Review the refund policy for Design Flow Digitals Shopify themes, apps, and custom development services.'
  });
  return <LegalLayout title="Refund Policy" lastUpdated="March 17, 2026" data-id="element-1061">
      <h2 data-id="element-1062">1. Overview</h2>
      <p data-id="element-1063">
        At Design Flow Digitals, we strive to ensure complete satisfaction with
        our services and products. This Refund Policy outlines the terms and
        conditions for refunds on our Shopify themes, apps, and custom
        development services.
      </p>

      <h2 data-id="element-1064">2. Digital Products (Themes & Apps)</h2>
      <p data-id="element-1065">
        Due to the nature of digital products, all sales of Shopify themes and
        apps are generally final. However, we may offer a refund in the
        following circumstances:
      </p>
      <ul data-id="element-1066">
        <li data-id="element-1067">The product is materially different from its description.</li>
        <li data-id="element-1068">
          The product has a critical defect that cannot be resolved within a
          reasonable timeframe.
        </li>
        <li data-id="element-1069">Duplicate purchase made in error.</li>
      </ul>
      <p data-id="element-1070">
        Refund requests for digital products must be made within 14 days of
        purchase.
      </p>

      <h2 data-id="element-1071">3. Custom Development Services</h2>
      <p data-id="element-1072">For custom Shopify store design and development services:</p>
      <ul data-id="element-1073">
        <li data-id="element-1074">
          Refund eligibility depends on the stage of the project and the terms
          outlined in your specific service agreement.
        </li>
        <li data-id="element-1075">
          Work already completed and delivered is generally non-refundable.
        </li>
        <li data-id="element-1076">
          Deposits may be partially refundable if the project has not yet
          commenced.
        </li>
      </ul>

      <h2 data-id="element-1077">4. How to Request a Refund</h2>
      <p data-id="element-1078">
        To request a refund, please contact our support team at{' '}
        <a href="mailto:support@designflowdigitals.com" data-id="element-1079">
          support@designflowdigitals.com
        </a>{' '}
        with your order details and reason for the refund request. We will
        review your request and respond within 5 business days.
      </p>

      <h2 data-id="element-1080">5. Processing</h2>
      <p data-id="element-1081">
        Approved refunds will be processed to the original payment method within
        10 business days of approval.
      </p>
    </LegalLayout>;
}
export function CookiesPolicyPage() {
  usePageSEO({
    canonical: '/cookies-policy',
    keywords: 'cookies policy, Design Flow Digitals cookies, tracking policy',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Cookies Policy',
      path: '/cookies-policy'
    }]),
    title: 'Cookies Policy | Design Flow Digitals',
    description: 'Learn about how Design Flow Digitals uses cookies on our website to improve your browsing experience.'
  });
  return <LegalLayout title="Cookies Policy" lastUpdated="March 17, 2026" data-id="element-1082">
      <h2 data-id="element-1083">1. What Are Cookies</h2>
      <p data-id="element-1084">
        Cookies are small text files that are placed on your computer or mobile
        device by websites that you visit. They are widely used to make websites
        work more efficiently, as well as to provide reporting information to
        the owners of the site.
      </p>

      <h2 data-id="element-1085">2. How We Use Cookies</h2>
      <p data-id="element-1086">Design Flow Digitals uses cookies for the following purposes:</p>
      <ul data-id="element-1087">
        <li data-id="element-1088">
          <strong className="text-white" data-id="element-1089">Essential Cookies:</strong> These
          cookies are necessary for the website to function and cannot be
          switched off. They are usually only set in response to actions made by
          you, such as setting your privacy preferences or filling in forms.
        </li>
        <li data-id="element-1090">
          <strong className="text-white" data-id="element-1091">Analytics Cookies:</strong> These
          cookies allow us to count visits and traffic sources so we can measure
          and improve the performance of our site. They help us know which pages
          are the most and least popular.
        </li>
        <li data-id="element-1092">
          <strong className="text-white" data-id="element-1093">Functional Cookies:</strong> These
          cookies enable the website to provide enhanced functionality and
          personalisation. They may be set by us or by third party providers
          whose services we have added to our pages.
        </li>
      </ul>

      <h2 data-id="element-1094">3. Managing Cookies</h2>
      <p data-id="element-1095">
        Most web browsers allow you to control cookies through their settings
        preferences. However, if you limit the ability of websites to set
        cookies, you may worsen your overall user experience, since it will no
        longer be personalized to you.
      </p>

      <h2 data-id="element-1096">4. Third-Party Cookies</h2>
      <p data-id="element-1097">
        In some special cases, we also use cookies provided by trusted third
        parties. These third-party cookies may track your use of our website to
        help us improve our products and services.
      </p>

      <h2 data-id="element-1098">5. Contact Us</h2>
      <p data-id="element-1099">
        If you have any questions about our use of cookies, please contact us
        at:{' '}
        <a href="mailto:support@designflowdigitals.com" data-id="element-1100">
          support@designflowdigitals.com
        </a>
      </p>
    </LegalLayout>;
}